import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Button,
  Intent,
  Spinner,
  Icon,
  Tag,
  Classes
} from "@blueprintjs/core";
const DISPLAY_DURATION_MS = 6e4;
export default function BackgroundBuildIndicator({ pollInterval = 2500 }) {
  const [builds, setBuilds] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedBuildLogs, setExpandedBuildLogs] = useState({});
  const [dismissedIds, setDismissedIds] = useState(/* @__PURE__ */ new Set());
  const [currentTime, setCurrentTime] = useState(Date.now());
  const activePollRef = useRef(null);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1e3);
    return () => clearInterval(timer);
  }, []);
  const fetchBuilds = useCallback(async () => {
    try {
      const res = await axios.get("/api/background_builds");
      if (res.data && Array.isArray(res.data.builds)) {
        setBuilds(res.data.builds);
      }
    } catch (e) {
    }
  }, []);
  const buildsRef = useRef(builds);
  buildsRef.current = builds;
  useEffect(() => {
    let timeoutId;
    let isMounted = true;
    const poll = async () => {
      await fetchBuilds();
      if (!isMounted) return;
      const hasActive = buildsRef.current.some(
        (b) => b.status === "queued" || b.status === "building"
      );
      const intervalTime = hasActive ? 1e3 : pollInterval;
      timeoutId = setTimeout(poll, intervalTime);
    };
    poll();
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchBuilds, pollInterval]);
  const visibleBuilds = builds.filter((b) => {
    if (dismissedIds.has(b.id)) return false;
    if (b.status === "superseded") return false;
    if (b.status === "queued" || b.status === "building") return true;
    const finishedAtMs = (b.finished_at || b.created_at) * 1e3;
    const elapsed = currentTime - finishedAtMs;
    if (expandedBuildLogs[b.id]) return true;
    return elapsed < DISPLAY_DURATION_MS;
  });
  const activeBuilds = visibleBuilds.filter((b) => b.status === "queued" || b.status === "building");
  const completedBuilds = visibleBuilds.filter((b) => b.status === "success" || b.status === "error");
  const handleDismiss = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setDismissedIds((prev) => /* @__PURE__ */ new Set([...prev, id]));
  }, []);
  const handleDismissAll = useCallback((e) => {
    if (e) e.stopPropagation();
    const allIds = visibleBuilds.map((b) => b.id);
    setDismissedIds((prev) => /* @__PURE__ */ new Set([...prev, ...allIds]));
  }, [visibleBuilds]);
  const handleOpenBuildDialog = useCallback((build, e) => {
    if (e) e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent("open-build-output-dialog", {
        detail: {
          build_output: build.build_output,
          alignment_output: build.alignment_output,
          error: build.error,
          title: build.trigger_source || "Operation Results"
        }
      })
    );
  }, []);
  const toggleLogView = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setExpandedBuildLogs((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  }, []);
  if (visibleBuilds.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": "background-build-indicator",
      style: {
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1500,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            "data-testid": "background-build-bubble",
            onClick: () => setIsExpanded((prev) => !prev),
            style: {
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "20px",
              background: activeBuilds.length > 0 ? "#106ba3" : "#2d3748",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              cursor: "pointer",
              userSelect: "none",
              fontSize: "13px",
              fontWeight: "500",
              transition: "all 0.2s ease"
            },
            title: "Click to toggle background build results",
            children: [
              activeBuilds.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(Spinner, { size: 16, intent: Intent.WARNING }),
                /* @__PURE__ */ jsxs("span", { children: [
                  "Background Build (",
                  activeBuilds.length,
                  " active)"
                ] })
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(Icon, { icon: "tick-circle", intent: Intent.SUCCESS, size: 16 }),
                /* @__PURE__ */ jsxs("span", { children: [
                  "Build Complete (",
                  visibleBuilds.length,
                  ")"
                ] })
              ] }),
              /* @__PURE__ */ jsx(
                Icon,
                {
                  icon: isExpanded ? "chevron-down" : "chevron-up",
                  size: 14,
                  style: { opacity: 0.8, marginLeft: "4px" }
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  minimal: true,
                  small: true,
                  icon: "cross",
                  style: { color: "#fff", minHeight: "16px", minWidth: "16px", padding: 0 },
                  title: "Dismiss all build notifications",
                  onClick: handleDismissAll
                }
              )
            ]
          }
        ),
        isExpanded && /* @__PURE__ */ jsxs(
          "div",
          {
            "data-testid": "background-build-panel",
            style: {
              position: "absolute",
              bottom: "45px",
              right: "0px",
              width: "420px",
              maxHeight: "480px",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
              border: "1px solid #d1d5db",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "10px"
            },
            children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #e5e7eb",
                    paddingBottom: "8px",
                    marginBottom: "2px"
                  },
                  children: [
                    /* @__PURE__ */ jsxs("span", { style: { fontWeight: 600, fontSize: "14px", color: "#1f2937" }, children: [
                      "Background Builds (",
                      visibleBuilds.length,
                      ")"
                    ] }),
                    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: "6px" }, children: [
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          small: true,
                          minimal: true,
                          text: "Dismiss All",
                          onClick: handleDismissAll
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          small: true,
                          minimal: true,
                          icon: "cross",
                          onClick: () => setIsExpanded(false)
                        }
                      )
                    ] })
                  ]
                }
              ),
              visibleBuilds.map((build) => {
                const finishedAtMs = (build.finished_at || build.created_at) * 1e3;
                const secondsAgo = Math.max(0, Math.round((currentTime - finishedAtMs) / 1e3));
                const remainingSeconds = Math.max(0, Math.round((DISPLAY_DURATION_MS - (currentTime - finishedAtMs)) / 1e3));
                const hasLogs = !!(build.build_output || build.alignment_output || build.error);
                const isLogOpen = !!expandedBuildLogs[build.id];
                let statusTagIntent = Intent.NONE;
                let statusIcon = "time";
                let statusText = "Queued";
                if (build.status === "building") {
                  statusTagIntent = Intent.PRIMARY;
                  statusIcon = "refresh";
                  statusText = "Building...";
                } else if (build.status === "success") {
                  statusTagIntent = Intent.SUCCESS;
                  statusIcon = "tick-circle";
                  statusText = "Completed";
                } else if (build.status === "error") {
                  statusTagIntent = Intent.DANGER;
                  statusIcon = "error";
                  statusText = "Failed";
                }
                return /* @__PURE__ */ jsxs(
                  "div",
                  {
                    "data-testid": `build-item-${build.id}`,
                    style: {
                      background: "#f9fafb",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                      padding: "10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px"
                    },
                    children: [
                      /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
                        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "6px" }, children: [
                          /* @__PURE__ */ jsxs(
                            Tag,
                            {
                              minimal: true,
                              intent: statusTagIntent,
                              icon: build.status === "building" ? void 0 : statusIcon,
                              style: { fontSize: "11px", fontWeight: "600" },
                              children: [
                                build.status === "building" && /* @__PURE__ */ jsx(Spinner, { size: 10, style: { marginRight: 4 } }),
                                statusText
                              ]
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "a",
                            {
                              href: "#",
                              "data-testid": `build-link-${build.id}`,
                              onClick: (e) => {
                                e.preventDefault();
                                handleOpenBuildDialog(build, e);
                              },
                              style: {
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "#106ba3",
                                textDecoration: "none",
                                maxWidth: "220px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer"
                              },
                              onMouseEnter: (e) => e.currentTarget.style.textDecoration = "underline",
                              onMouseLeave: (e) => e.currentTarget.style.textDecoration = "none",
                              title: `Click to open full build dialogue for: ${build.trigger_source || "Background Build"}`,
                              children: build.trigger_source || "Background Build"
                            }
                          )
                        ] }),
                        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "4px" }, children: [
                          build.status === "success" && /* @__PURE__ */ jsxs("span", { style: { fontSize: "11px", color: "#6b7280" }, title: "Auto-closes after 60s", children: [
                            remainingSeconds,
                            "s"
                          ] }),
                          /* @__PURE__ */ jsx(
                            Button,
                            {
                              minimal: true,
                              small: true,
                              icon: "cross",
                              title: "Dismiss this build",
                              onClick: (e) => handleDismiss(build.id, e)
                            }
                          )
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { style: { fontSize: "11px", color: "#6b7280", display: "flex", gap: "10px" }, children: [
                        build.duration !== null && build.duration !== void 0 && /* @__PURE__ */ jsxs("span", { children: [
                          "Duration: ",
                          /* @__PURE__ */ jsxs("strong", { children: [
                            build.duration,
                            "s"
                          ] })
                        ] }),
                        build.status !== "building" && build.status !== "queued" && /* @__PURE__ */ jsxs("span", { children: [
                          "Finished: ",
                          secondsAgo,
                          "s ago"
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: "6px", alignItems: "center" }, children: [
                        /* @__PURE__ */ jsx(
                          Button,
                          {
                            small: true,
                            minimal: true,
                            intent: Intent.PRIMARY,
                            icon: "application",
                            text: build.type === "git_commit" ? "Open Commit Dialogue" : "Open Build Dialogue",
                            onClick: (e) => handleOpenBuildDialog(build, e),
                            style: { padding: "0px 4px", height: "22px", fontSize: "11px" },
                            title: build.type === "git_commit" ? "Open standard commit dialogue with full stdout for commit tasks" : "Open standard build dialogue with full stdout for build tasks"
                          }
                        ),
                        hasLogs && /* @__PURE__ */ jsx(
                          Button,
                          {
                            small: true,
                            minimal: true,
                            icon: isLogOpen ? "chevron-up" : "chevron-down",
                            text: isLogOpen ? "Hide Inline" : "View Inline",
                            onClick: (e) => toggleLogView(build.id, e),
                            style: { padding: "0px 4px", height: "22px", fontSize: "11px", color: "#6b7280" }
                          }
                        )
                      ] }),
                      isLogOpen && /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            marginTop: "4px",
                            background: "#1e1e1e",
                            color: "#d4d4d4",
                            padding: "8px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontFamily: "monospace",
                            maxHeight: "180px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                          },
                          children: build.type === "git_commit" ? /* @__PURE__ */ jsxs("div", { children: [
                            build.build_output && /* @__PURE__ */ jsxs("div", { children: [
                              /* @__PURE__ */ jsx("strong", { style: { color: "#4ec9b0" }, children: "Git Output:" }),
                              /* @__PURE__ */ jsx("div", { style: { marginTop: "2px" }, children: build.build_output })
                            ] }),
                            build.error && /* @__PURE__ */ jsxs("div", { style: { color: "#f87171", marginTop: build.build_output ? "6px" : "0px" }, children: [
                              /* @__PURE__ */ jsx("strong", { children: "Error:" }),
                              " ",
                              build.error
                            ] })
                          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                            build.build_output && /* @__PURE__ */ jsxs("div", { children: [
                              /* @__PURE__ */ jsx("strong", { style: { color: "#4ec9b0" }, children: "Assembly Build Output:" }),
                              /* @__PURE__ */ jsx("div", { style: { marginTop: "2px" }, children: build.build_output })
                            ] }),
                            build.alignment_output && /* @__PURE__ */ jsxs("div", { style: { marginTop: "6px" }, children: [
                              /* @__PURE__ */ jsx("strong", { style: { color: "#9cdcfe" }, children: "Alignment Output:" }),
                              /* @__PURE__ */ jsx("div", { style: { marginTop: "2px" }, children: build.alignment_output })
                            ] }),
                            build.error && /* @__PURE__ */ jsxs("div", { style: { color: "#f87171" }, children: [
                              /* @__PURE__ */ jsx("strong", { children: "Error:" }),
                              " ",
                              build.error
                            ] })
                          ] })
                        }
                      )
                    ]
                  },
                  build.id
                );
              })
            ]
          }
        )
      ]
    }
  );
}
